import BaseAuthProvider from './BaseAuthProvider'
import {AuthProviders} from '@/domain/login/constant/authProviders'

/**
 * Apple ID authentication provider
 */
export default class AppleAuthProvider extends BaseAuthProvider {
    /**
     * Load Apple SDK
     */
    async loadSDK() {
        const appleClientId = process.env.VUE_APP_APPLE_APP_ID

        return new Promise((resolve) => {
            const script = document.createElement("script")
            script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
            script.async = true
            script.defer = true
            script.onload = () => {
                // Initialize Apple ID
                window.AppleID.auth.init({
                    clientId: appleClientId,
                    scope: "name email",
                    redirectURI: location.origin,
                    state: '',
                    usePopup: true
                })
                resolve()
            }
            document.body.appendChild(script)
        })
    }

    /**
     * Start Apple login flow and get ID token
     * @returns {Promise<string>} ID token
     */
    async startLogin() {
        const authResponse = await window.AppleID.auth.signIn()

        if (authResponse && authResponse.authorization) {
            return authResponse.authorization.id_token
        }

        throw new Error('Apple login failed: no authorization data')
    }
    /**
     * Authenticate with Apple ID token
     * @param {Object} credentials - {token, language?, allowRegister?}
     * @returns {Promise<Object>} {token: access_token, type: token_type, jwt}
     */
    async authenticate(credentials) {
        const {token, language, allowRegister = false} = credentials

        const params = {
            backend: 'apple-id', // Backend API expects this format
            token,
            grant_type: 'convert_token',
            client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
            is_dashboard: true
        }

        if (language) {
            params.language = language
        }

        if (allowRegister) {
            params.allow_register = true
        }

        const result = await this.service.convertAuthToken(params)

        return {
            token: result.access_token,
            type: result.token_type,
            jwt: result.jwt
        }
    }

    async logout(tokenData) {
        // Apple ID auth doesn't need server-side logout
    }

    supportsRefresh() {
        return false
    }

    getMethod() {
        return AuthProviders.APPLE
    }
}
